/* BI-14 · ÉQUIPE — GESTION
   Removing someone never removes their attribution from history, and a
   Workspace always has exactly one Propriétaire.

   D-46 / 0018 (batch 13): the invitation carries a role and the establishments
   it reaches. The picker is required for the scoped roles — an empty list is
   nothing, not everything — and hidden for a single-establishment workspace,
   where the one establishment is implicit. Changing the role to Propriétaire or
   Administrateur clears the list on screen: an unscoped role must never carry a
   stale list that could narrow it back.
   D-31: the pending rows are read through the establishment context. */
import { useState } from 'react'
import { Button, Card, Checkbox, Field, List, ListRow, PageHeader, Rule, Select } from '@/ui'
import { useLocations } from '@/lib/api'
import { ALL, useScope } from '@/lib/scope'
import { actsIn, needsLocations, ROLE_LABEL, ROLE_ORDER, type TeamRole } from './api'
import { useBill } from './ParamShell'

export default function BI14Gestion() {
  const b = useBill()
  const [scope] = useScope()
  const { data: places } = useLocations()
  const all = places ?? []
  const many = all.length > 1

  /* the invitation the block edits — the first pending row, the one still
     waiting for its role */
  const invite = b.pending[0]
  const [role, setRole] = useState<TeamRole>(invite?.role ?? 'staff')
  const [picked, setPicked] = useState<string[]>(
    invite?.locations ?? (scope === ALL ? [] : [scope]))

  const askFor = () => (picked.length ? picked : scope === ALL ? [] : [scope])
  const changeRole = (r: TeamRole) => { setRole(r); setPicked(needsLocations(r) ? askFor() : []) }
  const toggle = (id: string) =>
    setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]))

  const rows = b.pending.filter(r => !r.role || actsIn(r.role, r.locations ?? [], scope, ALL))
  const missing = needsLocations(role) && many && picked.length === 0

  return (
    <section data-frame="BI-14" style={{ display: 'grid', gap: 14 }}>
      <PageHeader title="Équipe — gestion" />
      <List>
        {rows.map(r => (
          <ListRow key={r.id} title={r.title} meta={r.meta}
            end={<>{r.actions.map((a, i) => (
              <span key={a}>{i > 0 && ' · '}<Button variant="ghost" size="sm">{a}</Button></span>
            ))}</>} />
        ))}
      </List>

      <Rule>rôle et établissements de l’invitation</Rule>
      <Field label="Rôle"
        help={needsLocations(role)
          ? many
            ? undefined
            : 'Manager et Collaborateur ne voient que leurs établissements. Vous n’en avez qu’un : il est attribué d’office.'
          : 'Propriétaire et Administrateur voient tout l’espace : aucun établissement à choisir.'}>
        {id => (
          <Select id={id} value={role} onChange={e => changeRole(e.target.value as TeamRole)}>
            {ROLE_ORDER.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </Select>
        )}
      </Field>
      {needsLocations(role) && many && (
        <div className="field">
          <span className="f-label">Établissements</span>
          <List>
            {all.map(l => (
              <ListRow key={l.id} title={l.name}
                end={<Checkbox checked={picked.includes(l.id)} aria-label={l.name}
                  onChange={() => toggle(l.id)} />} />
            ))}
          </List>
          <span className="f-help">
            {missing
              ? 'Choisissez au moins un établissement : sans établissement, cette personne ne voit rien.'
              : 'Au moins un établissement — cette personne ne voit que ceux-là.'}
          </span>
        </div>
      )}

      <Card>
        <strong>Transférer la propriété</strong> — vers un membre existant : code frais
        (vérification renforcée) → le destinataire accepte → vous devenez Administrateur. Un
        Workspace a toujours exactement un Propriétaire.
      </Card>
    </section>
  )
}
